import { stashSkillDraft } from "@/shared/domain/skills";
import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "./approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  toolSuccess,
} from "./ClientTools";
import { coerceToSkillDraft, type CreateSkillInput } from "./createSkillCoerce";
import { CreateSkillConfirmation } from "./CreateSkillConfirmation";

// `coerceToSkillDraft` lives in `createSkillCoerce` to stay out of the
// tool ⇄ confirmation import cycle; re-exported here so the co-located test
// can import the normalization contract alongside `tool`.
export { coerceToSkillDraft, type CreateSkillInput };

// ---------------------------------------------------------------------------
// JSON Schema
// ---------------------------------------------------------------------------

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    name: {
      type: "string",
      description:
        "Short slug-style name for the skill (required). Letters, digits, " +
        "hyphens, and underscores only — no spaces. Must be unique in the org.",
    },
    description: {
      type: "string",
      description:
        "The 'when to use' text (required, <=500 chars): a one-line summary of " +
        "when the AI should reach for this skill. Shown in the skill registry.",
    },
    body: {
      type: "string",
      description:
        "The procedure itself (required): the full instructions the AI follows " +
        "when the skill is invoked. May be multi-line / Markdown.",
    },
    accessibility: {
      type: "string",
      enum: ["private", "org", "org-editable"],
      description:
        "Who can see / edit the skill. 'private' (default): only the author. " +
        "'org': visible to the whole org, author-only edits. 'org-editable': " +
        "visible to the org and editable by any subscribed member.",
    },
    tags: {
      type: "array",
      items: { type: "string" },
      description:
        "Optional free-form labels for categorization (e.g. 'qa-review', 'triage').",
    },
  },
  required: ["name", "description", "body"],
};

// ---------------------------------------------------------------------------
// Execute
//
// Unlike most tools, this one does NOT persist. It stashes a draft and
// navigates the user to the Skills page, which opens the create editor
// pre-filled — mirroring `client_create_agent`. The skill isn't created until
// the user clicks Save in that editor, so the editor (not this tool) is the
// final validation seam.
// ---------------------------------------------------------------------------

function execute(
  input: CreateSkillInput,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  const draft = coerceToSkillDraft(input);
  if (draft === null) {
    return toolError(
      "A skill draft needs at least a name, a description, or a body.",
    );
  }
  stashSkillDraft(draft);
  deps.goto.navigateTo(Goto.route.AgentSkills);
  return toolSuccess("opened_skill_editor_with_draft", {
    navigated_to: Goto.route.AgentSkills,
    name: draft.name ?? null,
  });
}

// ---------------------------------------------------------------------------
// Tool module
// ---------------------------------------------------------------------------

export const tool = createClientTool<CreateSkillInput>({
  name: "client_create_skill",
  description:
    "Open the Skills editor pre-filled with a drafted AI Skill — a stored, " +
    "versioned procedure the AI can invoke later — based on the current " +
    "conversation. Use this when the user asks to save the current workflow / " +
    "instructions as a skill, or explicitly asks you to create one. Provide a " +
    "slug-style 'name', a 'description' (when to use it), and a 'body' (the " +
    "procedure). The user lands in the editor to review, refine, and save — " +
    "this tool does NOT persist the skill.",
  input_schema: inputSchema,
  execute,
  // Navigation-only (no persistence), so follow the user's Auto toggle by
  // default — same as the sibling `client_create_agent` draft tool.
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Open skill editor with draft",
  displayDescription:
    "Opens the Skills editor pre-filled with a drafted skill. The skill isn't saved until you click Save in the editor.",
  ConfirmationContent: CreateSkillConfirmation,
});
